/**
 * Compresses an image file client-side before uploading.
 * This function uses the HTML Canvas API to resize and re-encode the image.
 *
 * @param {File} file The original image file object from an input element.
 * @param {number} [targetSizeMB=1.5] The desired maximum file size in megabytes.
 * @returns {Promise<File>} A promise that resolves with the new, compressed File object.
 *                          If the original file is already smaller than the target size,
 *                          it resolves with the original file.
 */
export const compressImage = (file, targetSizeMB = 1.5) => {
  // Return a new Promise. This is necessary because image loading and processing
  // are asynchronous operations. The promise will either 'resolve' with the
  // compressed file or 'reject' if an error occurs.
  return new Promise((resolve, reject) => {
    // Convert the target size from Megabytes (MB) to bytes for accurate comparison.
    const targetSizeBytes = targetSizeMB * 1024 * 1024;

    // --- OPTIMIZATION STEP ---
    // If the original image is already smaller than our target, there's no need
    // to perform any compression. We can immediately resolve the promise with the
    // original file, saving processing time.
    if (file.size <= targetSizeBytes) {
      console.log("Image is already small enough. Skipping compression.");
      resolve(file);
      return; // Exit the function early.
    }

    // --- STEP 1: READ THE FILE ---
    // Use the FileReader API to read the contents of the image file.
    const reader = new FileReader();
    // readAsDataURL converts the image file into a base64 encoded string.
    // This string can be used as the `src` for an <img> element.
    reader.readAsDataURL(file);

    // Define what happens when the FileReader successfully reads the file.
    reader.onload = (event) => {
      // --- STEP 2: LOAD THE IMAGE IN MEMORY ---
      // Create a new, in-memory Image object. This is like creating an <img> tag
      // in HTML, but without adding it to the webpage's DOM.
      const img = new Image();
      // Set the source of our image object to the base64 string from the FileReader.
      // This will trigger the browser to decode and load the image data.
      img.src = event.target.result;

      // Define what happens when the image has been fully loaded and decoded.
      img.onload = () => {
        // --- STEP 3: PREPARE THE CANVAS ---
        // Create an in-memory <canvas> element. The canvas will be our virtual
        // drawing board where we will redraw the image at a smaller size.
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        // --- STEP 4: CALCULATE NEW DIMENSIONS (RESIZING) ---
        // Resizing the image dimensions is the most effective way to reduce file size.
        // We'll set a maximum width/height to prevent overly large images.
        const MAX_WIDTH_OR_HEIGHT = 1920; // A common resolution like 1080p is a good cap.
        let { width, height } = img; // Get original image dimensions.

        // Check if the image is wider than it is tall (landscape) or vice-versa (portrait).
        if (width > height) {
          // If the width is greater than our maximum allowed width...
          if (width > MAX_WIDTH_OR_HEIGHT) {
            // ...calculate the new height to maintain the original aspect ratio.
            height *= MAX_WIDTH_OR_HEIGHT / width;
            // And set the width to the maximum.
            width = MAX_WIDTH_OR_HEIGHT;
          }
        } else {
          // If the height is greater than our maximum allowed height...
          if (height > MAX_WIDTH_OR_HEIGHT) {
            // ...calculate the new width to maintain the original aspect ratio.
            width *= MAX_WIDTH_OR_HEIGHT / height;
            // And set the height to the maximum.
            height = MAX_WIDTH_OR_HEIGHT;
          }
        }

        // Apply the newly calculated (or original, if small enough) dimensions to our canvas.
        canvas.width = width;
        canvas.height = height;

        // --- STEP 5: DRAW THE IMAGE ON THE CANVAS ---
        // Use the drawImage method to paint the original image onto our canvas
        // at the new, smaller dimensions. This effectively creates a resized copy.
        ctx.drawImage(img, 0, 0, width, height);

        // --- STEP 6: EXPORT THE COMPRESSED IMAGE FROM THE CANVAS ---
        // The toBlob() method converts the canvas content into a file-like Blob object.
        // This is where the actual JPEG compression happens.
        canvas.toBlob(
          (blob) => {
            // This is an asynchronous callback that runs once the blob is created.
            if (!blob) {
              // If blob creation fails for some reason, reject the promise.
              reject(new Error("Canvas to Blob conversion failed."));
              return;
            }

            // --- STEP 7: CREATE A NEW FILE OBJECT ---
            // A Blob is just raw data. A File object is a Blob with extra metadata
            // like a name and lastModified date, which is what upload APIs usually expect.
            // We create a new File from our compressed blob, preserving the original file name.
            const compressedFile = new File([blob], file.name, {
              type: "image/jpeg",      // Explicitly set the MIME type.
              lastModified: Date.now(), // Set the last modified date to now.
            });

            // --- FINAL STEP: RESOLVE THE PROMISE ---
            // The compression is successful! Resolve the promise with the new, smaller file.
            resolve(compressedFile);
          },
          "image/jpeg", // Specify the output format. 'image/png' is also an option.
          0.8           // Specify the image quality (a value between 0 and 1).
                        // 0.8 (80%) is a good balance of quality and size reduction.
                        // You can lower this (e.g., to 0.7) for more compression.
        );
      };

      // Set up an error handler for the image loading process.
      img.onerror = (error) => reject(error);
    };

    // Set up an error handler for the FileReader process.
    reader.onerror = (error) => reject(error);
  });
};